import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "./BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import type { Ordre } from "types/ordre";
import type { Vehicle } from "types/garage/vehicules";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import type { Org } from "types/org";

function InfoRow({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-row items-baseline gap-2 py-1">
      <span className="text-h4 text-neutral-400 min-w-[120px] shrink-0">{label}</span>
      <span className="text-h3 text-black">{value || "—"}</span>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-h2 font-semibold text-neutral-500 uppercase tracking-wide mt-3 mb-1">
      {children}
    </div>
  );
}

export default function ContratLocationModal({
  ordre,
  rentalVehicle,
  contact,
  clientVehicle,
  orgData,
  onClose,
}: {
  ordre: Ordre;
  rentalVehicle: Vehicle;
  contact?: Contact | null;
  clientVehicle?: ClientVehicle | null;
  orgData: Org;
  onClose: () => void;
}) {
  const [generating, setGenerating] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [edlUrl, setEdlUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"info" | "contrat" | "edl">("info");

  const usage = ordre.mobility_option === "location" || rentalVehicle.usage === "location" ? "location" : "pret";
  const usageLabel = usage === "location" ? "Location" : "Pret";

  // Parse dates for display
  const formatDate = (ts?: { seconds: number }) => {
    if (!ts) return "";
    const d = new Date(ts.seconds * 1000);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  };
  const formatTime = (ts?: { seconds: number }) => {
    if (!ts) return "";
    const d = new Date(ts.seconds * 1000);
    return `${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const dateDepart = formatDate(ordre.reception_date) || new Date().toLocaleDateString("fr-FR");
  const heureDepart = formatTime(ordre.reception_date);
  const dateRetour = formatDate(ordre.restitution);
  const heureRetour = formatTime(ordre.restitution);

  const contactName = [contact?.civilite, contact?.nom, contact?.prenom].filter(Boolean).join(" ");

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await ordresApi.generateContratLocation(ordre.id);
      if (result.documents?.length >= 2) {
        setPdfUrl(result.documents[0].url);
        setEdlUrl(result.documents[1].url);
        setActiveTab("contrat");
      } else if (result.documents?.length === 1) {
        setPdfUrl(result.documents[0].url);
        setActiveTab("contrat");
      }
      const updatedOrdre = await ordresApi.get(ordre.id);
      useOrdreStore.getState().updateOrdre(updatedOrdre);
      showNotification({ type: "success", message: "Documents generees" });
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la generation" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <BaseModal
      title={`Contrat de ${usageLabel}`}
      startIcon={<Icon1 name="document" size={20} />}
      onClose={onClose}
      width={pdfUrl ? "max-w-5xl" : "max-w-xl"}
    >
      <div className="flex flex-col" style={{ maxHeight: "85vh" }}>
        {/* Tabs when PDFs are generated */}
        {pdfUrl && (
          <div className="flex flex-row gap-1 p-2 border-b border-neutral-200 bg-neutral-50">
            <button
              className={`px-3 py-1.5 rounded-md text-h3 font-medium transition-colors ${activeTab === "info" ? "bg-white shadow-sm text-black" : "text-neutral-400 hover:text-black"}`}
              onClick={() => setActiveTab("info")}
            >
              Informations
            </button>
            <button
              className={`px-3 py-1.5 rounded-md text-h3 font-medium transition-colors ${activeTab === "contrat" ? "bg-white shadow-sm text-black" : "text-neutral-400 hover:text-black"}`}
              onClick={() => setActiveTab("contrat")}
            >
              Contrat
            </button>
            {edlUrl && (
              <button
                className={`px-3 py-1.5 rounded-md text-h3 font-medium transition-colors ${activeTab === "edl" ? "bg-white shadow-sm text-black" : "text-neutral-400 hover:text-black"}`}
                onClick={() => setActiveTab("edl")}
              >
                Etat des lieux
              </button>
            )}
          </div>
        )}

        {/* Info tab */}
        {activeTab === "info" && (
          <div className="flex flex-col gap-1 p-4 overflow-y-auto">
            <SectionTitle>Vehicule de {usageLabel}</SectionTitle>
            <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
              <InfoRow label="Marque" value={rentalVehicle.marque} />
              <InfoRow label="Modele" value={rentalVehicle.modele} />
              <InfoRow label="Immatriculation" value={rentalVehicle.plate} />
              <InfoRow label="Couleur" value={rentalVehicle.coloris} />
              <InfoRow label="Carburant" value={rentalVehicle.fuel_type} />
              <InfoRow label="Km" value={rentalVehicle.km ? String(rentalVehicle.km) : undefined} />
            </div>

            <SectionTitle>Client</SectionTitle>
            <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
              <InfoRow label="Nom" value={contactName || ordre.name} />
              <InfoRow label="Telephone" value={contact?.phone} />
              <InfoRow label="Email" value={contact?.email} />
            </div>

            {clientVehicle && (
              <>
                <SectionTitle>Vehicule en reparation</SectionTitle>
                <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
                  <InfoRow label="Vehicule" value={`${clientVehicle.marque || ""} ${clientVehicle.modele || ""}`} />
                  <InfoRow label="Immatriculation" value={clientVehicle.plate} />
                  <InfoRow label="Ordre" value={ordre.num} />
                </div>
              </>
            )}

            <SectionTitle>Dates</SectionTitle>
            <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
              <InfoRow label="Date depart" value={dateDepart} />
              {heureDepart && <InfoRow label="Heure depart" value={heureDepart} />}
              <InfoRow label="Date retour" value={dateRetour || "Non definie"} />
              {heureRetour && <InfoRow label="Heure retour" value={heureRetour} />}
            </div>

            <SectionTitle>Etablissement</SectionTitle>
            <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
              <InfoRow label="Nom" value={orgData.infos?.name} />
              <InfoRow label="Adresse" value={orgData.infos?.address} />
              <InfoRow label="Ville" value={[orgData.infos?.postal, orgData.infos?.city].filter(Boolean).join(" ")} />
              <InfoRow label="Telephone" value={orgData.infos?.phone_number} />
            </div>

            <div className="flex flex-row gap-2 mt-4">
              {!pdfUrl ? (
                <Button
                  label={generating ? "Generation en cours..." : "Generer les documents"}
                  onClick={handleGenerate}
                  disabled={generating}
                  loading={generating}
                />
              ) : (
                <SecondaryButton
                  label="Re-generer"
                  onClick={handleGenerate}
                  disabled={generating}
                />
              )}
            </div>
          </div>
        )}

        {/* Contrat PDF tab */}
        {activeTab === "contrat" && pdfUrl && (
          <div className="flex-1" style={{ minHeight: "70vh" }}>
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              style={{ minHeight: "70vh" }}
              title="Contrat de location"
            />
          </div>
        )}

        {/* Etat des lieux PDF tab */}
        {activeTab === "edl" && edlUrl && (
          <div className="flex-1" style={{ minHeight: "70vh" }}>
            <iframe
              src={edlUrl}
              className="w-full h-full border-0"
              style={{ minHeight: "70vh" }}
              title="Etat des lieux"
            />
          </div>
        )}
      </div>
    </BaseModal>
  );
}
